import { useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import { TransformControls as DreiTransformControls } from '@react-three/drei';
import * as THREE from 'three';
import { Item, ItemType, TransformableItem } from '@/types/core/Item';
import { useAppContext } from '@/context/AppContext';

/**
 * TransformControls component for manipulating 3D objects in the scene
 * Provides controls for translating, rotating, and scaling objects
 */
interface TransformControlsProps {
  selectedItem: Item | null;
  transformMode: 'translate' | 'rotate' | 'scale';
  orbitControlsRef: React.RefObject<any>;
}

// Types of items that can be transformed
const TRANSFORMABLE_TYPES: ItemType[] = ['mesh', 'light', 'group'];

export const TransformControls = ({
  selectedItem,
  transformMode,
  orbitControlsRef
}: TransformControlsProps) => {
  const { camera } = useThree();
  const { updateItem } = useAppContext();
  
  // Check if the selected item can be transformed
  const isTransformable = selectedItem && 
    TRANSFORMABLE_TYPES.includes(selectedItem.type);
  
  // Get the 3D object reference
  const object = selectedItem?.threeRef?.current || undefined;
  
  // Update the item's transform properties in the app state
  const handleTransformChange = useCallback(() => {
    if (!selectedItem?.threeRef?.current) return;

    const object = selectedItem.threeRef.current;
    // Update the matrix before getting position/rotation/scale
    object.updateMatrix();
    object.updateMatrixWorld();
    
    const position = new THREE.Vector3();
    const rotation = new THREE.Euler();
    const scale = new THREE.Vector3();

    object.getWorldPosition(position);
    object.getWorldScale(scale);
    rotation.setFromRotationMatrix(object.matrixWorld);

    // Update the item with new transform values
    updateItem(selectedItem.id, {
      properties: {
        transform: { position, rotation, scale }
      }
    });
  }, [selectedItem, updateItem]);

  // Disable orbit controls when transform controls are being used
  const handleMouseDown = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
  }, [orbitControlsRef]);

  // Re-enable orbit controls when transform controls are released
  const handleMouseUp = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = true;
    }
    // Ensure transform changes are applied when mouse is released
    handleTransformChange();
  }, [orbitControlsRef, handleTransformChange]);

  // Don't render anything if there's no transformable item selected
  if (!isTransformable || !object) {
    return null;
  }

  return (
    <DreiTransformControls
      object={object}
      mode={transformMode}
      camera={camera}
      onChange={handleTransformChange}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      space="world"
    />
  );
}; 